import React, { useState } from 'react';
import { InputField, FilterDropdown } from '../../../components';
import {
  AlreadyHaveCompanyRegister,
  // AppLevelBudget,
  BySignIn,
  RegisterCompany,
  Register,
  SignIn,
  TAndC,
  // TenDollerFree,
  // UserLevelBudget,
  countries,
} from '../data/constants';
import './signup.css';
import makeToast from '../../../components/toaster';
import { registerCompany } from '../../service';
import {
  validateEmail,
  validateText,
  validateTextFieldForSpecialCharsAndNum,
  validateWebsite,
} from '../../../services';

interface SignUpPageProps {
  setSignUpPageOpen: React.Dispatch<boolean>;
}

function SignUpPage({ setSignUpPageOpen }: SignUpPageProps) {
  const [formData, setFormData] = useState<Record<string, string>>({
    company_name: '',
    company_website: '',
    email: '',
    first_name: '',
    last_name: '',
    country: '',
  });
  const [errorInfo, setErrorInfo] = useState<Record<string, string>>({
    company_name: '',
    email: '',
    first_name: '',
    company_website: '',
    last_name: '',
    country: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'company_name':
        return validateTextFieldForSpecialCharsAndNum(value, 'Company Name');
      case 'first_name':
        return validateTextFieldForSpecialCharsAndNum(value, 'First Name');
      case 'last_name':
        return validateTextFieldForSpecialCharsAndNum(value, 'Last Name', false);
      case 'country':
        return validateText(value, 'Country');
      case 'email':
        return validateEmail(value);
      case 'company_website':
        return validateWebsite(value);
      default:
        return '';
    }
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    const errorMsg = validateField(name, value);
    setErrorInfo((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};

    (['company_name', 'email', 'first_name', 'country'] as const).forEach((field) => {
      const value = formData[field];
      const errorMsg = validateField(field, value);
      if (errorMsg) {
        errors[field] = errorMsg;
      }
    });

    if (Object.keys(errors).length > 0) {
      setErrorInfo(errors);
      return;
    }

    // Simulate async API submission
    setIsLoading(true);
    try {
      await registerCompany(formData)?.then((response) => {
        if (response?.status === 200) {
          makeToast('success', 'Company registered successfully!');
          setTimeout(() => {
            setSignUpPageOpen(false);
          }, 2000);
          setFormData({
            company_name: '',
            company_website: '',
            email: '',
            first_name: '',
            last_name: '',
          });
        }
      });
    } catch (error: unknown) {
      makeToast(
        'error',
        (error as { response: { data: { message: string } } })?.response?.data?.message ||
          'An unexpected error occurred.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="flex flex-col w-full py-4 md:max-w-lg mx-auto">
      {' '}
      {/* Adjusted for responsiveness: w-full on small, max-w-lg and centered on md+ */}
      <form
        className="form-section bg-no-repeat bg-cover mr-0 ml-0 bg-[#f5f5f5] mt-4 h-full py-[1.5rem] px-4 sm:px-6 md:px-7 rounded-xl border-[3px] border-[#A8C5DA] gap-[1.25rem]" // Corrected: Responsive padding and removed problematic alignment classes
        onSubmit={handleSubmit}
      >
        <h2 className="font-bold text-2xl mb-2">{RegisterCompany}</h2>

        {/* Credit Banner - Adjusted gap for responsiveness */}
        {/* <div className="flex gap-4 sm:gap-10 relative bg-gradient-to-t from-orange-500 via-orange-400 to-orange-300 rounded-md text-xs p-1 text-center font-semibold shadow-md ">
          <div className="flex relative justify-center items-center px-2 bg-white rounded-md font-bold text-sm z-[2]">
            {TenDollerFree}
            <div className="triangle" />{' '}
          </div>
          {/* The responsiveness of 'triangle' depends on its CSS definition, which is not provided in this snippet. 
          <div className="flex flex-col text-[12px] text-white font-bold">
            <span>{UserLevelBudget}</span>
            <span>{AppLevelBudget}</span>
          </div>
        </div> */}

        {/* Inputs - Adjusted to stack on small screens, side-by-side on medium and larger */}
        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <div className="w-full sm:w-1/2">
            {' '}
            {/* Takes full width on small screens, half width on sm and up */}
            <InputField
              label="Company Name"
              type="text"
              placeholder="XYZ Organisation"
              name="company_name"
              value={formData.company_name}
              handleChange={handleFieldChange}
              error={errorInfo.company_name}
            />
          </div>
          <div className="w-full sm:w-1/2">
            {' '}
            {/* Takes full width on small screens, half width on sm and up */}
            <InputField
              label="Company Website"
              type="text"
              placeholder="www.xyz.com"
              name="company_website"
              value={formData.company_website}
              handleChange={handleFieldChange}
              isRequired={false}
              error={errorInfo.company_website}
            />
          </div>
        </div>

        <InputField
          label="Business Email"
          type="text"
          tooltip="Allows only valid work emails"
          placeholder="Example@domain.com"
          name="email"
          value={formData.email}
          handleChange={handleFieldChange}
          error={errorInfo.email}
        />

        {/* Inputs - Adjusted to stack on small screens, side-by-side on medium and larger */}
        <div className="flex flex-col sm:flex-row gap-3 mb-2">
          <div className="w-full sm:w-1/2">
            {' '}
            {/* Takes full width on small screens, half width on sm and up */}
            <InputField
              label="First Name"
              type="text"
              placeholder="John"
              name="first_name"
              value={formData.first_name}
              handleChange={handleFieldChange}
              error={errorInfo.first_name}
            />
          </div>
          <div className="w-full sm:w-1/2">
            {' '}
            {/* Takes full width on small screens, half width on sm and up */}
            <InputField
              label="Last Name"
              type="text"
              placeholder="Doe"
              name="last_name"
              value={formData.last_name}
              handleChange={handleFieldChange}
              isRequired={false}
              error={errorInfo.last_name}
            />
          </div>
        </div>

        <div className="w-full flex flex-col gap-1 mb-4">
          <span className="block text-xs font-bold text-gray-800">
            Country <span className="text-red-500">*</span>
          </span>
          <FilterDropdown
            options={countries.map((c) => ({ value: c.label, label: c.label }))}
            value={formData.country}
            onChange={(v: string) =>
              handleFieldChange({
                target: { name: 'country', value: v },
              } as React.ChangeEvent<HTMLSelectElement>)
            }
            placeholder="Select a country"
            showClear={false}
            className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border ${errorInfo.country ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md shadow-sm w-full px-3 py-2 text-left flex items-center justify-between`}
          />
          {errorInfo.country && <p className="text-red-500 text-xs">{errorInfo.country}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`${
            isLoading ? 'opacity-70 cursor-not-allowed' : ''
          } bg-[#0d85bd] text-[#fff] rounded-lg py-2.5 px-10 select-none w-full`}
        >
          {isLoading ? 'Registering...' : Register}
        </button>

        <span className="flex text-sm text-gray-500 mt-3 w-full text-center items-center justify-center gap-1">
          {BySignIn}{' '}
          <a
            href="https://www.grazitti.com/legal/terms-and-conditions/"
            target="_blank"
            className="text-[#0d85bd] hover:underline cursor-pointer font-bold"
            rel="noreferrer"
          >
            {TAndC}
          </a>
        </span>

        <span className="flex text-sm text-gray-500 mt-1 w-full text-center items-center justify-center gap-1">
          <span>{AlreadyHaveCompanyRegister}</span>
          <button
            type="button"
            onClick={() => {
              setSignUpPageOpen(false);
            }}
            className="text-orange-500 font-bold hover:underline hover:cursor-pointer"
          >
            {SignIn}
          </button>
        </span>
      </form>
    </section>
  );
}

export default SignUpPage;
